/**
 * Firestore `companyUsers` / `companies` client security constants (main app).
 * Mirror: backend/src/config/companyUserFirestoreSecurity.ts
 */

export const COMPANY_USER_FIRESTORE_USER_TYPES = {
  BROKER: 'broker',
  SHIPPER: 'shipper',
  CARRIER: 'carrier',
  BROKER_CARRIER: 'broker_carrier',
} as const;

export const COMPANY_USER_CROSS_READ_BROKER_SIDE_TYPES = [
  COMPANY_USER_FIRESTORE_USER_TYPES.BROKER,
  COMPANY_USER_FIRESTORE_USER_TYPES.SHIPPER,
  COMPANY_USER_FIRESTORE_USER_TYPES.BROKER_CARRIER,
] as const;

export const COMPANY_USER_CROSS_READ_CARRIER_TARGET_TYPES = [
  COMPANY_USER_FIRESTORE_USER_TYPES.CARRIER,
] as const;

export const COMPANY_USER_CROSS_READ_CARRIER_COUNTERPARTY_TYPES = [
  COMPANY_USER_FIRESTORE_USER_TYPES.BROKER,
  COMPANY_USER_FIRESTORE_USER_TYPES.SHIPPER,
] as const;

export const COMPANY_USER_SERVER_ONLY_FIELD_KEYS = [
  'approvalStatus',
  'isAdmin',
  'isSuperAdmin',
  'permissions',
  'accessLevel',
  'role',
  'companyUserRole',
  'companyId',
  'promoTrialStartedAt',
  'promoTrialEndsAt',
  'promoCode',
  'rejectedAt',
  'rejectedBy',
  'rejectionReason',
  'approvedAt',
  'approvedBy',
  'adminInactiveAt',
  'adminInactiveBy',
  'adminInactiveReason',
  'passportTransportOnly',
  'registrationSource',
  'adminRegistrationNotifyDeferTo',
  'brokerAuthorityVerified',
] as const;

export type CompanyUserServerOnlyFieldKey = (typeof COMPANY_USER_SERVER_ONLY_FIELD_KEYS)[number];

/** Subcollection under users/{uid} and companyUsers/{uid}. Rules: userPartnersSubdocMayRead/Write in firestore.rules. */
export const COMPANY_USER_PARTNERS_SUBCOLLECTION = 'partners' as const;

/**
 * Phase 1: cross-type profile reads (broker→carrier, carrier→broker, etc.) are served only via
 * `/api/tranzit/carrier-directory/*` (Admin SDK). Client Firestore rules allow self, same company, admin.
 *
 * Portal UI isolation (broker / shipper / carrier shells) is enforced client-side via
 * `src/utils/portalAccessUtils.ts` and `usePortalShellGuard` — not via Firestore rules.
 */
export const COMPANY_USER_CLIENT_CROSS_TYPE_READ_VIA_API_ONLY = true as const;

/** Legacy top-level collection (A2). Canonical profile: `companyUsers`. */
export const LEGACY_USERS_COLLECTION = 'users' as const;

/** Same-company field on legacy `users/{uid}` docs (`companyUsers` uses `companyId`). */
export const LEGACY_USER_SAME_COMPANY_FIELD = 'parentCompanyId' as const;

/** A2: client must not rely on arbitrary authenticated `users/` reads; cross-type via API. */
export const LEGACY_USERS_CLIENT_READ_SCOPED = true as const;

/** `firestore.rules` helper — keep name in sync with rules file. */
export const LEGACY_USER_PROFILE_READ_RULE_HELPER = 'legacyUserProfileReadAllowed' as const;
